/**
 * Select Receipt Modal Component
 *
 * Modal to select a validated goods receipt for creating a supplier return.
 * Shows only receipts with status VALIDATED.
 */

import { useState, useMemo } from 'react';
import { Search } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { Modal } from '@/components/ui';
import { Input } from '@/components/ui';
import { Button } from '@/components/ui';
import { Skeleton } from '@/components/ui';
import { EmptyState } from '@/components/ui';
import { useCurrency, useFormatDate } from '@/hooks';
import { formatCentimes } from '@/lib/format';
import { goodsReceiptsService } from '@/features/goods-receipts/services/goods-receipts.service';
import { GoodsReceiptStatus } from '@bstock/shared';
import type { GoodsReceiptListItem } from '@bstock/shared';

interface SelectReceiptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelect: (receiptId: string) => void;
}

export function SelectReceiptModal({ isOpen, onClose, onSelect }: SelectReceiptModalProps) {
  const currency = useCurrency();
  const { formatDateShort } = useFormatDate();
  const [search, setSearch] = useState('');

  const [page, setPage] = useState(1);

  // Fetch validated receipts only when modal is open
  const { data: receiptsData, isLoading } = useQuery({
    queryKey: ['goods-receipts', 'validated-for-return', page, search],
    queryFn: () =>
      goodsReceiptsService.list({
        status: GoodsReceiptStatus.VALIDATED,
        limit: 20,
        page,
        sortBy: 'receiptDate',
        sortOrder: 'desc',
      }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const receipts = receiptsData?.data ?? [];
  const pagination = receiptsData?.pagination;

  // Filter receipts locally based on search
  const filteredReceipts = useMemo(() => {
    if (!receipts.length) return [];
    if (!search.trim()) return receipts;

    const searchLower = search.toLowerCase().trim();
    return receipts.filter(
      (receipt) =>
        receipt.receiptNumber.toLowerCase().includes(searchLower) ||
        receipt.supplier.name.toLowerCase().includes(searchLower)
    );
  }, [receipts, search]);

  const handleClose = () => {
    setSearch('');
    setPage(1);
    onClose();
  };

  const handleSelect = (receipt: GoodsReceiptListItem) => {
    setSearch('');
    onSelect(receipt.id);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Selectionner une reception"
      size="2xl"
    >
      <div className="space-y-4">
        {/* Search input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par n° ou fournisseur..."
            className="pl-10"
          />
        </div>

        {/* Receipts list */}
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          {/* Table header */}
          <div className="grid grid-cols-12 gap-4 px-4 py-3 bg-neutral-50 border-b border-neutral-200 text-sm font-medium text-neutral-600">
            <div className="col-span-3">N° Reception</div>
            <div className="col-span-3">Fournisseur</div>
            <div className="col-span-2">Date</div>
            <div className="col-span-2 text-right">Montant HT</div>
            <div className="col-span-2 text-center">Lignes</div>
          </div>

          {/* Table body */}
          <div className="max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="p-4 space-y-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-12" />
                ))}
              </div>
            ) : filteredReceipts.length === 0 ? (
              <div className="p-8">
                <EmptyState
                  title={search ? 'Aucune reception trouvee' : 'Aucune reception disponible'}
                  description={
                    search
                      ? 'Aucune reception ne correspond a votre recherche'
                      : 'Il n\'y a pas de reception validee disponible'
                  }
                />
              </div>
            ) : (
              filteredReceipts.map((receipt) => (
                <button
                  key={receipt.id}
                  type="button"
                  onClick={() => handleSelect(receipt)}
                  className="w-full grid grid-cols-12 gap-4 px-4 py-3 text-sm hover:bg-neutral-50 border-b border-neutral-100 last:border-b-0 transition-colors text-left"
                >
                  <div className="col-span-3 font-medium text-dark-500">
                    {receipt.receiptNumber}
                  </div>
                  <div className="col-span-3 text-neutral-700 truncate">
                    {receipt.supplier.name}
                  </div>
                  <div className="col-span-2 text-neutral-600">
                    {formatDateShort(receipt.receiptDate)}
                  </div>
                  <div className="col-span-2 text-right text-neutral-700">
                    {formatCentimes(receipt.totalExclTax, currency)}
                  </div>
                  <div className="col-span-2 text-center text-neutral-600">
                    {receipt.itemsCount}
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Pagination + Footer */}
        <div className="flex items-center justify-between">
          <div className="text-xs text-neutral-500">
            {pagination ? `${pagination.total} reception(s)` : ''}
          </div>
          <div className="flex items-center gap-2">
            {pagination && pagination.totalPages > 1 && (
              <>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page <= 1}>
                  Precedent
                </Button>
                <span className="text-sm text-neutral-600">{page} / {pagination.totalPages}</span>
                <Button variant="ghost" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= pagination.totalPages}>
                  Suivant
                </Button>
              </>
            )}
            <Button variant="ghost" onClick={handleClose}>
              Annuler
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
